import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import adminSchemaApi from '../api/adminSchemaApi';
import MultiSelectDropdown from '../../components/layout/MultiSelectDropdown1';
import ObjectPermissionMatrix from '../../components/common/ObjectPermissionMatrix';
import {
  ADMIN_UI_LOCKED_ALLOWED_OBJECT_KEYS,
  allowedObjectsSelectedForPlanUi,
  applyTelephonyPackageToggle,
  DEFAULT_PLAN_CONFIG,
  filterSearchViewAllowedObjects,
  getPackageRequiredObjectNames,
  getStandardTabPackageKey,
  isStandardObjectPackageRequired,
  isStandardTabPackageRequired,
  isAlwaysMigrateObject,
  isExcludedFromSearchViewObjectPicklist,
  isStandardTabNotGatedByAllowedObjects,
  isSearchViewCustomObjectKey,
  mergeAlwaysIncludedAllowedObjects,
  normalizeAllowedObjects,
  normalizePlanConfig,
  removeAllowedObjectsForPackage,
  SETUP_OPTIONS,
  STANDARD_OBJECT_OPTIONS,
  splitSearchViewPicklistByTenantSchema,
  getDisabledPackageObjectDenySet,
  stripDisabledPackageObjects,
  REAL_ESTATE_PACKAGE_OBJECT_NAMES,
  VISIT_MANAGER_PACKAGE_OBJECT_NAMES,
} from './AdminPlanConfigConstants';
import { isAdminModuleFullPlanEditor } from '../utils/adminSession';
import StorageLimitsFields from './StorageLimitsFields';
import '../styles/admin-theme.css';

const AdminPlanConfig = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [config, setConfig] = useState(DEFAULT_PLAN_CONFIG);
  const [originalConfig, setOriginalConfig] = useState(DEFAULT_PLAN_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [standardObjects, setStandardObjects] = useState([]);
  const [standardTabs, setStandardTabs] = useState([]);

  const loadPlan = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError('');
      const data = await adminSchemaApi.fetchPlanById(id);
      setPlan(data);
      if (data) {
        let normalized = normalizePlanConfig(data.config);
        if (!isAdminModuleFullPlanEditor()) {
          normalized = { ...normalized, setup: { ...normalized.setup, showSetup: false } };
        }
        setConfig(normalized);
        setOriginalConfig(normalized);
      }
      const packages = data?.config?.enabledPackages ?? [];
      const [objects, tabs] = await Promise.all([
        adminSchemaApi.fetchStandardObjects(packages).catch(() => []),
        adminSchemaApi.fetchStandardTabs(packages).catch(() => []),
      ]);
      setStandardObjects(Array.isArray(objects) ? objects : []);
      setStandardTabs(Array.isArray(tabs) ? tabs : []);
    } catch (err) {
      setError('Failed to load plan.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const handlePackageToggle = useCallback(
    async (packageKey, checked) => {
      const next = (() => {
        let n = applyTelephonyPackageToggle(config.enabledPackages ?? [], packageKey, checked);
        if (checked && packageKey === 'email_campaign') {
          n = [...new Set([...n, 'watconnect'])];
        }
        return n;
      })();

      const tabsBefore = standardTabs;
      const objectsBefore = standardObjects;

      const [objects, tabs] = await Promise.all([
        adminSchemaApi.fetchStandardObjects(next).catch(() => []),
        adminSchemaApi.fetchStandardTabs(next).catch(() => []),
      ]);
      const objs = Array.isArray(objects) ? objects : [];
      const tbs = Array.isArray(tabs) ? tabs : [];
      setStandardObjects(objs);
      setStandardTabs(tbs);

      const pkLc = String(packageKey || '').toLowerCase().trim();

      if (checked) {
        const packageTabIdsToAdd = tbs
          .filter((t) => getStandardTabPackageKey(t) === pkLc)
          .filter((t) => isStandardTabPackageRequired(t))
          .map((t) => t.id)
          .filter(Boolean);
        const namesFromApi = objs
          .filter((o) => String(o?.config?.package ?? '').toLowerCase().trim() === pkLc)
          .filter((o) => isStandardObjectPackageRequired(o))
          .map((o) => o.object_name)
          .filter(Boolean);
        const extraEmail =
          packageKey === 'email_campaign' ? ['email_campaign', 'email_campaign_member'] : [];
        const extraVisitManager =
          packageKey === 'visit_manager' ? [...VISIT_MANAGER_PACKAGE_OBJECT_NAMES] : [];
        const extraRealEstate =
          packageKey === 'real_estate' ? [...REAL_ESTATE_PACKAGE_OBJECT_NAMES] : [];
        const namesToAdd = [...new Set([...namesFromApi, ...extraEmail, ...extraVisitManager, ...extraRealEstate])];

        setConfig((prev) => ({
          ...prev,
          enabledPackages: next,
          allowedObjectsPermission: mergeAlwaysIncludedAllowedObjects(
            [...new Set([...(prev.allowedObjectsPermission ?? []), ...namesToAdd])]
          ),
          objectManager: {
            ...prev.objectManager,
            allowedObjects: mergeAlwaysIncludedAllowedObjects(
              [...new Set([...(prev.objectManager?.allowedObjects ?? []), ...namesToAdd])]
            ),
          },
          ...(packageTabIdsToAdd.length > 0
            ? { tab: { ...prev.tab, allowedTabs: [...new Set([...(prev.tab?.allowedTabs ?? []), ...packageTabIdsToAdd])] } }
            : {}),
        }));
      } else {
        const packageTabIdsToRemove = new Set(
          (tabsBefore || [])
            .filter((t) => getStandardTabPackageKey(t) === pkLc)
            .map((t) => t.id)
            .filter(Boolean)
        );
        setConfig((prev) => {
          let allowedPermission = removeAllowedObjectsForPackage(
            prev.allowedObjectsPermission ?? [],
            objectsBefore,
            packageKey
          );
          let allowedOm = removeAllowedObjectsForPackage(
            prev.objectManager?.allowedObjects ?? [],
            objectsBefore,
            packageKey
          );
          if (packageKey === 'email_campaign') {
            allowedPermission = allowedPermission.filter((o) => o !== 'email_campaign' && o !== 'email_campaign_member');
            allowedOm = allowedOm.filter((o) => o !== 'email_campaign' && o !== 'email_campaign_member');
          }
          if (packageKey === 'visit_manager') {
            const rm = new Set(VISIT_MANAGER_PACKAGE_OBJECT_NAMES);
            allowedPermission = allowedPermission.filter((o) => !rm.has(o));
            allowedOm = allowedOm.filter((o) => !rm.has(o));
          }
          if (packageKey === 'real_estate') {
            const rm = new Set(REAL_ESTATE_PACKAGE_OBJECT_NAMES);
            allowedPermission = allowedPermission.filter((o) => !rm.has(o));
            allowedOm = allowedOm.filter((o) => !rm.has(o));
          }
          const allowedTabs = (prev.tab?.allowedTabs ?? []).filter((id) => !packageTabIdsToRemove.has(id));
          return {
            ...prev,
            enabledPackages: next,
            tab: { ...prev.tab, allowedTabs },
            allowedObjectsPermission: mergeAlwaysIncludedAllowedObjects(allowedPermission),
            objectManager: {
              ...prev.objectManager,
              allowedObjects: mergeAlwaysIncludedAllowedObjects(allowedOm),
            },
          };
        });
      }
    },
    [config.enabledPackages, standardObjects, standardTabs]
  );

  React.useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const packageRequiredObjectNames = useMemo(
    () => getPackageRequiredObjectNames(standardObjects, config.enabledPackages ?? []),
    [standardObjects, config.enabledPackages]
  );

  useEffect(() => {
    const raw = config.allowedObjectsPermission ?? config.objectManager?.allowedObjects ?? [];
    const next = stripDisabledPackageObjects(config.enabledPackages ?? [], raw);
    if (next.length === raw.length && next.every((x, i) => x === raw[i])) return;
    setConfig(prev => {
      const merged = mergeAlwaysIncludedAllowedObjects(next);
      const lc = prev.leadConversionMapping?.allowedObjects ?? ['account', 'contact', 'opportunity'];
      const nextLc = lc.filter(o => merged.includes(o));
      const resolved =
        nextLc.length > 0
          ? nextLc
          : ['account', 'contact', 'opportunity'].filter(o => merged.includes(o));
      return {
        ...prev,
        allowedObjectsPermission: merged,
        objectManager: { ...prev.objectManager, allowedObjects: merged },
        leadConversionMapping: { ...prev.leadConversionMapping, allowedObjects: resolved },
      };
    });
  }, [config.enabledPackages, config.allowedObjectsPermission, config.objectManager?.allowedObjects]);

  React.useEffect(() => {
    if (!standardObjects.length) return;
    const req = getPackageRequiredObjectNames(standardObjects, config.enabledPackages ?? []);
    if (!req.length) return;
    const cur = config.allowedObjectsPermission ?? [];
    const missing = req.filter((n) => !cur.includes(n));
    if (!missing.length) return;
    setConfig((prev) => ({
      ...prev,
      allowedObjectsPermission: mergeAlwaysIncludedAllowedObjects(
        [...new Set([...(prev.allowedObjectsPermission ?? []), ...missing])]
      ),
      objectManager: {
        ...prev.objectManager,
        allowedObjects: mergeAlwaysIncludedAllowedObjects(
          [...new Set([...(prev.objectManager?.allowedObjects ?? []), ...missing])]
        ),
      },
    }));
  }, [standardObjects, config.enabledPackages, config.allowedObjectsPermission]);

  const handleSave = async () => {
    setError('');
    setMessage('');
    try {
      setSaving(true);
      const req = getPackageRequiredObjectNames(standardObjects, config.enabledPackages ?? []);
      let perm = mergeAlwaysIncludedAllowedObjects(
        [...new Set([...req, ...(config.allowedObjectsPermission ?? [])])]
      );
      perm = mergeAlwaysIncludedAllowedObjects(stripDisabledPackageObjects(config.enabledPackages, perm));
      let om = mergeAlwaysIncludedAllowedObjects(
        [...new Set([...req, ...(config.objectManager?.allowedObjects ?? [])])]
      );
      om = mergeAlwaysIncludedAllowedObjects(stripDisabledPackageObjects(config.enabledPackages, om));
      const toSave = {
        ...config,
        setup: {
          ...config.setup,
          ...(isAdminModuleFullPlanEditor() ? {} : { showSetup: false }),
        },
        allowedObjectsPermission: perm,
        objectManager: {
          ...config.objectManager,
          allowedObjects: om,
        },
        searchView: config.searchView
          ? {
            ...config.searchView,
            allowedObjects: stripDisabledPackageObjects(
              config.enabledPackages,
              filterSearchViewAllowedObjects(config.searchView.allowedObjects ?? [])
            ),
          }
          : config.searchView,
      };
      await adminSchemaApi.updatePlan(id, { config: toSave });
      setMessage('Plan config saved.');
      setConfig(toSave);
      setOriginalConfig(toSave);
      setEditMode(false);
    } catch (err) {
      setError(err?.message || 'Failed to save plan config.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setConfig(originalConfig);
    setEditMode(false);
    setMessage('');
    setError('');
  };

  const handleAllowedObjectsChange = (section, values) => {
    setConfig((prev) => ({
      ...prev,
      [section]: { ...prev[section], allowedObjects: values },
    }));
  };

  const handleAllowedObjectsPermissionChange = (values) => {
    setConfig((prev) => {
      const locked = getPackageRequiredObjectNames(standardObjects, prev.enabledPackages ?? []);
      const merged = mergeAlwaysIncludedAllowedObjects([...new Set([...locked, ...(values ?? [])])]);
      const currentLeadConversion = prev.leadConversionMapping?.allowedObjects ?? ['account', 'contact', 'opportunity'];
      const nextLeadConversion = currentLeadConversion.filter((o) => merged.includes(o));
      const resolved =
        nextLeadConversion.length > 0
          ? nextLeadConversion
          : ['account', 'contact', 'opportunity'].filter((o) => merged.includes(o));
      return {
        ...prev,
        allowedObjectsPermission: merged,
        objectManager: { ...prev.objectManager, allowedObjects: merged },
        leadConversionMapping: { ...prev.leadConversionMapping, allowedObjects: resolved },
      };
    });
  };

  const isReadOnly = !editMode || loading;

  const standardPicklistOptions = useMemo(() => {
    let options;
    if (standardObjects.length > 0) {
      options = standardObjects.map((o) => {
        const name = o.object_name || '';
        const fromConfig = o.config?.display_label || o.config?.label;
        const label =
          (fromConfig && String(fromConfig).trim()) ||
          name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        return { key: name, value: label || name };
      });
    } else {
      options = STANDARD_OBJECT_OPTIONS.map((o) => ({ key: o.name, value: o.label }));
    }
    const packageDeny = getDisabledPackageObjectDenySet(config.enabledPackages);
    return options
      .filter((o) => !isAlwaysMigrateObject(o.key))
      .filter((o) => !packageDeny.has(String(o.key || '').toLowerCase().trim()));
  }, [standardObjects, config.enabledPackages]);

  const allowedObjectsPicklistOptions = useMemo(() => {
    const packageDeny = getDisabledPackageObjectDenySet(config.enabledPackages);
    const base = standardPicklistOptions;
    const selected = config.allowedObjectsPermission ?? config.objectManager?.allowedObjects ?? [];
    const keySet = new Set(base.map((o) => o.key));
    const extra = selected
      .filter(
        k =>
          k &&
          !isAlwaysMigrateObject(k) &&
          !keySet.has(k) &&
          !packageDeny.has(String(k).toLowerCase().trim())
      )
      .map((k) => ({ key: k, value: (k || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim() || k }));
    return extra.length > 0 ? [...base, ...extra] : base;
  }, [
    standardPicklistOptions,
    config.allowedObjectsPermission,
    config.objectManager?.allowedObjects,
    config.enabledPackages,
  ]);

  const leadConversionPicklistOptions = useMemo(() => {
    const allowed = config.allowedObjectsPermission ?? [];
    return standardPicklistOptions.filter((o) => allowed.includes(o.key));
  }, [standardPicklistOptions, config.allowedObjectsPermission]);

  const scoringRulePicklistOptions = useMemo(() => {
    const allowed = config.allowedObjectsPermission ?? [];
    return standardPicklistOptions.filter((o) => allowed.includes(o.key));
  }, [standardPicklistOptions, config.allowedObjectsPermission]);

  const searchViewLeadOptionsRaw = useMemo(() => {
    const allowed = config.allowedObjectsPermission ?? [];
    return allowedObjectsPicklistOptions.filter((o) => allowed.includes(o.key));
  }, [allowedObjectsPicklistOptions, config.allowedObjectsPermission]);

  const searchViewLeadOptions = useMemo(
    () => searchViewLeadOptionsRaw.filter((o) => o?.key && !isExcludedFromSearchViewObjectPicklist(o.key)),
    [searchViewLeadOptionsRaw]
  );

  const standardCatalogKeySet = useMemo(() => new Set(standardPicklistOptions.map((o) => o.key)), [standardPicklistOptions]);

  const { standard: searchViewStandardPicklist, custom: searchViewCustomPicklist } = useMemo(
    () => splitSearchViewPicklistByTenantSchema(searchViewLeadOptions, standardCatalogKeySet, undefined),
    [searchViewLeadOptions, standardCatalogKeySet]
  );

  const searchViewAllowed = config.searchView?.allowedObjects;
  const searchViewSelectedStandard = useMemo(
    () =>
      (searchViewAllowed ?? []).filter((k) => k && !isSearchViewCustomObjectKey(k, standardCatalogKeySet, undefined)),
    [searchViewAllowed, standardCatalogKeySet]
  );
  const searchViewSelectedCustom = useMemo(
    () => (searchViewAllowed ?? []).filter((k) => isSearchViewCustomObjectKey(k, standardCatalogKeySet, undefined)),
    [searchViewAllowed, standardCatalogKeySet]
  );

  const standardTabPicklistOptions = useMemo(() => {
    const allowedObjectNames = config.allowedObjectsPermission ?? [];
    const enabledPackages = config.enabledPackages ?? [];
    const enabledPkgLc = new Set(enabledPackages.map(p => String(p || '').toLowerCase().trim()).filter(Boolean));
    return standardTabs
      .filter((t) => {
        const tabPackage = getStandardTabPackageKey(t);
        const packageOk = !tabPackage || enabledPkgLc.has(tabPackage);
        if (!packageOk) return false;
        return (
          isStandardTabNotGatedByAllowedObjects(t) ||
          !t.object_name ||
          allowedObjectNames.includes(t.object_name)
        );
      })
      .map((t) => {
        const displayName = t.label || (t.name || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || t.name;
        const formatKey = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        const suffixParts = [t.object_name, t.config?.package].filter(Boolean).map(formatKey);
        const label = suffixParts.length > 0 ? `${displayName} (${suffixParts.join(', ')})` : displayName;
        return { key: t.id, value: label };
      });
  }, [standardTabs, config.allowedObjectsPermission, config.enabledPackages]);

  const packageTabIds = useMemo(() => {
    const pkgSet = new Set((config.enabledPackages ?? []).map((p) => String(p).toLowerCase().trim()));
    return (standardTabs || [])
      .filter((t) => {
        const pkg = getStandardTabPackageKey(t);
        return pkg && pkgSet.has(pkg) && isStandardTabPackageRequired(t);
      })
      .map((t) => t.id)
      .filter(Boolean);
  }, [standardTabs, config.enabledPackages]);

  const tabAllowedTabsSelected = useMemo(() => {
    const validIds = new Set(standardTabPicklistOptions.map((o) => o.key));
    const saved = config.tab?.allowedTabs ?? [];
    const merged = [...new Set([...packageTabIds, ...saved])].filter((id) => validIds.has(id));
    if (merged.length > 0) return merged;
    return standardTabPicklistOptions.map((o) => o.key);
  }, [config.tab?.allowedTabs, standardTabPicklistOptions, packageTabIds]);
  if (!id) {
    navigate('/admin/plans', { replace: true });
    return null;
  }

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <Link to="/admin/plans" className="btn btn-outline-secondary btn-sm me-2">
            ← Plans
          </Link>
          <Link to={`/admin/plans/${id}/detail`} className="btn btn-outline-info btn-sm me-2">
            Detail & prices
          </Link>
          <Link to={`/admin/plans/${id}/licenses`} className="btn btn-outline-primary btn-sm me-2">
            Licenses
          </Link>
          <h2 className="admin-title mb-1 d-inline">Plan config</h2>
          {plan && (
            <p className="admin-subtitle mb-0">
              {plan.name || plan.code} ({plan.code})
            </p>
          )}
        </div>
        <div className="d-flex gap-2">
          {editMode ? (
            <>
              <Button className="admin-primary-btn" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button variant="secondary" onClick={handleCancel} disabled={saving}>
                Cancel
              </Button>
            </>
          ) : (
            <Button
              className="admin-primary-btn"
              onClick={() => setEditMode(true)}
              disabled={loading || saving}
            >
              Edit
            </Button>
          )}
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}

      {loading ? (
        <div className="admin-subtitle">Loading...</div>
      ) : !plan ? (
        <div className="admin-subtitle">Plan not found.</div>
      ) : (
        <>
          {isAdminModuleFullPlanEditor() && (
            <Row className="g-3">
              <Col xs={12}>
                <Card className="p-3">
                  <h5>Setup</h5>
                  <Form.Check
                    type="switch"
                    label="Show setup"
                    checked={config.setup?.showSetup !== false}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        setup: { ...prev.setup, showSetup: e.target.checked },
                      }))
                    }
                    disabled={isReadOnly}
                    className="mb-2"
                  />
                  <Form.Text className="d-block mb-0 text-muted small">
                    When off, tenants do not get the Setup menu (allowed setup items still apply when you turn this on). The configuration sections below stay available here for editing. Plan licenses show Setup as on for display only.
                  </Form.Text>
                </Card>
              </Col>
            </Row>
          )}

          <Row className="g-3">
            <Col md={6}>
              <Card className="p-3">
                <h5>Packages</h5>
                <Form.Text className="d-block mb-2 text-muted small">
                  Enable packages for this plan. Standard objects that belong to a package only appear in the list below when that package is enabled.
                  More than one CTI / click-to-call package can be enabled.
                </Form.Text>
                <Form.Check
                  type="switch"
                  id="plan-package-watconnect"
                  label="WatConnect"
                  checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('watconnect')}
                  onChange={(e) => handlePackageToggle('watconnect', e.target.checked)}
                  disabled={isReadOnly}
                />
                <Form.Text className="text-muted small d-block mt-1">
                  With WatConnect enabled, also select <strong>Watconnect Ads Settings</strong> under Allowed Objects to enable Meta Lead Ads mapping.
                </Form.Text>
                <Form.Check
                  type="switch"
                  id="plan-package-watconnect-email-campaign"
                  className="mt-2"
                  label="Email Campaign"
                  checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('email_campaign')}
                  onChange={(e) => handlePackageToggle('email_campaign', e.target.checked)}
                  disabled={isReadOnly}
                />
                <Form.Text className="text-muted small d-block mt-1">
                  When enabled, users can send email from list view; email_campaign and email_campaign_member are available.
                </Form.Text>
                <Form.Check
                  type="switch"
                  id="plan-package-visit-manager"
                  className="mt-2"
                  label="Visit Manager"
                  checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('visit_manager')}
                  onChange={(e) => handlePackageToggle('visit_manager', e.target.checked)}
                  disabled={isReadOnly}
                />
                <Form.Text className="text-muted small d-block mt-1">
                  When enabled, Visit Plan, Visit Plan Template, Visit Stop, Visit Stop Template, and Visit Template objects and tabs are selected by default.
                </Form.Text>
                <Form.Check
                  type="switch"
                  id="plan-package-real-estate"
                  className="mt-2"
                  label="Real Estate"
                  checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('real_estate')}
                  onChange={(e) => handlePackageToggle('real_estate', e.target.checked)}
                  disabled={isReadOnly}
                />
                <Form.Text className="text-muted small d-block mt-1">
                  When enabled, Property, Floor, Unit, and Property Interest objects and tabs are selected by default. Contract stays a core CRM object.
                </Form.Text>
                <Form.Check
                  type="switch"
                  id="plan-package-telecmi"
                  className="mt-3"
                  label="TeleCMI (click-to-call)"
                  checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('telecmi')}
                  onChange={(e) => handlePackageToggle('telecmi', e.target.checked)}
                  disabled={isReadOnly}
                />
                <Form.Check
                  type="switch"
                  id="plan-package-callyzer"
                  className="mt-2"
                  label="Callyzer"
                  checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('callyzer')}
                  onChange={(e) => handlePackageToggle('callyzer', e.target.checked)}
                  disabled={isReadOnly}
                />
                <Form.Check
                  type="switch"
                  id="plan-package-vgrow-dialer"
                  className="mt-2"
                  label="zemneo Dialer"
                  checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('vgrow_dialer')}
                  onChange={(e) => handlePackageToggle('vgrow_dialer', e.target.checked)}
                  disabled={isReadOnly}
                />
                <Form.Check
                  type="switch"
                  id="plan-package-mis"
                  className="mt-2"
                  label="MIS Integration"
                  checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('mis')}
                  onChange={(e) => handlePackageToggle('mis', e.target.checked)}
                  disabled={isReadOnly}
                />

                <Form.Text className="text-muted small d-block mt-1">
                  TeleCMI, Callyzer, MIS Integration and zemneo Dialer can each be toggled independently.
                </Form.Text>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="p-3">
                <h5>Allowed Objects Permission</h5>
                <Form.Text className="d-block mb-2 text-muted small">
                  Objects the user can access. Only these objects are visible in tabs and configurable (with or without Setup). If user has no Setup, they still see and use only these objects.
                </Form.Text>
                <MultiSelectDropdown
                  selectedValues={allowedObjectsSelectedForPlanUi(
                    config.enabledPackages,
                    config.allowedObjectsPermission ?? config.objectManager?.allowedObjects ?? []
                  )}
                  picklistValues={allowedObjectsPicklistOptions}
                  onChange={handleAllowedObjectsPermissionChange}
                  disabled={isReadOnly}
                  lockedKeys={[...packageRequiredObjectNames, ...ADMIN_UI_LOCKED_ALLOWED_OBJECT_KEYS]}
                  placeholder="Select objects user can access"
                />
                <Form.Text className="text-muted small d-block mt-1">
                  Objects required by an enabled package are selected automatically and cannot be cleared while that package is on. Activity, Task, Note, Email log, and Attachment are always allowed and stay selected. Product, Product price, User, Role, and Password reset are always included in migration and are not shown here.
                </Form.Text>
              </Card>
            </Col>
          </Row>

          <Row className="g-3">
            <Col xs={12}>
              <Card className="p-3">
                <h5>Setup Configuration</h5>
                <Form.Text className="d-block mb-3 text-muted small">
                  Configure Setup Menu first (which items appear in Setup). Then Tab, Field, Layout, Workflow (if allowed), Scoring Rule, Assignment Rule, Web Forms, Search Views, Lead Conversion and Migration, Object Manager, and Object Permissions. Report and Dashboard are configured separately below (not part of Setup).
                </Form.Text>
                <Row className="g-3">
                  <Col md={6}>
                    <Card className="p-3 bg-light">
                      <h6 className="mb-2">Setup Menu</h6>
                      <Form.Group className="mb-2">
                        <Form.Label>Mode</Form.Label>
                        <Form.Select
                          value={config.setup.mode}
                          onChange={(e) =>
                            setConfig((prev) => ({
                              ...prev,
                              setup: { ...prev.setup, mode: e.target.value },
                            }))
                          }
                          disabled={isReadOnly}
                        >
                          <option value="allowlist">allowlist</option>
                          <option value="denylist">denylist</option>
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Allowed setup items</Form.Label>
                        <MultiSelectDropdown
                          picklistValues={SETUP_OPTIONS}
                          selectedValues={config.setup.allowedItems ?? []}
                          onChange={(values) =>
                            setConfig((prev) => ({
                              ...prev,
                              setup: { ...prev.setup, allowedItems: values },
                            }))
                          }
                          disabled={isReadOnly}
                          placeholder="Select setup items"
                        />
                      </Form.Group>
                      <Form.Text className="text-muted small">Which setup menu items are available. If Workflow Manager is not selected here, the Workflow section below is hidden and workflow is disabled for this plan.</Form.Text>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="p-3 bg-light">
                      <h6 className="mb-2">App Manager</h6>
                      <Form.Text className="d-block mb-2 text-muted small">Create app permission and limit. −1 = unlimited.</Form.Text>
                      <Form.Group className="mb-2">
                        <Form.Label>Max Apps</Form.Label>
                        <Form.Control
                          type="number"
                          min={-1}
                          value={config.app?.maxApps ?? -1}
                          onChange={(e) => {
                            const v = e.target.value;
                            const num = v === '' ? -1 : parseInt(v, 10);
                            if (!Number.isFinite(num)) return;
                            setConfig((prev) => ({ ...prev, app: { ...(prev.app || {}), maxApps: num } }));
                          }}
                          disabled={isReadOnly}
                        />
                        <Form.Text className="text-muted small">−1 = unlimited</Form.Text>
                      </Form.Group>
                      <Form.Check type="switch" label="Allow Create App" checked={config.app?.allowCreateApp !== false} onChange={(e) => setConfig((prev) => ({ ...prev, app: { ...(prev.app || {}), allowCreateApp: e.target.checked } }))} disabled={isReadOnly} />
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="p-3 bg-light">
                      <h6 className="mb-2">Tab Configuration</h6>
                      <Form.Text className="d-block mb-2 text-muted small">
                        Standard tabs available for this plan. Tabs whose object matches Allowed Objects Permission are shown. By default, matching tabs are selected.
                      </Form.Text>
                      <Form.Group className="mb-2">
                        <Form.Label>Allowed Standard Tabs</Form.Label>
                        <MultiSelectDropdown
                          picklistValues={standardTabPicklistOptions}
                          selectedValues={tabAllowedTabsSelected}
                          onChange={(values) => {
                            const requiredSet = new Set(packageTabIds);
                            const merged = [...packageTabIds, ...values.filter((id) => !requiredSet.has(id))];
                            setConfig((prev) => ({ ...prev, tab: { ...prev.tab, allowedTabs: merged } }));
                          }}
                          disabled={isReadOnly}
                          placeholder="Select standard tabs..."
                        />
                        <Form.Text className="text-muted small">
                          {packageTabIds.length > 0
                            ? 'Tabs for enabled packages (e.g. WatConnect, Email Campaign) are always included and cannot be removed.'
                            : 'Select which standard tabs are visible.'}
                        </Form.Text>
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Max Tabs</Form.Label>
                        <Form.Control
                          type="number"
                          min={-1}
                          value={config.tab.maxTabs}
                          onChange={(e) => {
                            const v = e.target.value;
                            const num = v === '' ? 0 : parseInt(v, 10);
                            if (!Number.isFinite(num)) return;
                            setConfig((prev) => ({ ...prev, tab: { ...prev.tab, maxTabs: num } }));
                          }}
                          disabled={isReadOnly}
                        />
                        <Form.Text className="text-muted small">−1 = unlimited</Form.Text>
                      </Form.Group>
                      <Form.Check type="switch" label="Allow Create" checked={config.tab.allowCreate} onChange={(e) => setConfig((prev) => ({ ...prev, tab: { ...prev.tab, allowCreate: e.target.checked } }))} disabled={isReadOnly} />
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="p-3 bg-light">
                      <h6 className="mb-2">Field Management</h6>
                      <Form.Group className="mb-2">
                        <Form.Label>Max Custom Fields</Form.Label>
                        <Form.Control
                          type="number"
                          value={config.field.maxCustom}
                          onChange={(e) =>
                            setConfig((prev) => ({
                              ...prev,
                              field: { ...prev.field, maxCustom: Number(e.target.value) || 0 },
                            }))
                          }
                          disabled={isReadOnly}
                        />
                      </Form.Group>
                      <Form.Check type="switch" label="Allow Create" checked={config.field.allowCreate} onChange={(e) => setConfig((prev) => ({ ...prev, field: { ...prev.field, allowCreate: e.target.checked } }))} disabled={isReadOnly} />
                      <Form.Check type="switch" label="Allow Standard Edit" checked={config.field.allowStandardEdit} onChange={(e) => setConfig((prev) => ({ ...prev, field: { ...prev.field, allowStandardEdit: e.target.checked } }))} disabled={isReadOnly} />
                      <Form.Check type="switch" label="Allow Standard Delete" checked={config.field.allowStandardDelete} onChange={(e) => setConfig((prev) => ({ ...prev, field: { ...prev.field, allowStandardDelete: e.target.checked } }))} disabled={isReadOnly} />
                      <Form.Check type="switch" label="Allow Edit Label" checked={config.field.allowEditLabel !== false} onChange={(e) => setConfig((prev) => ({ ...prev, field: { ...prev.field, allowEditLabel: e.target.checked } }))} disabled={isReadOnly} />
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="p-3 bg-light">
                      <h6 className="mb-2">Layout Settings</h6>
                      <Form.Group className="mb-2">
                        <Form.Label>Count Mode</Form.Label>
                        <Form.Select
                          value={config.layout.countMode}
                          onChange={(e) =>
                            setConfig((prev) => ({
                              ...prev,
                              layout: { ...prev.layout, countMode: e.target.value },
                            }))
                          }
                          disabled={isReadOnly}
                        >
                          <option value="pair">pair</option>
                          <option value="single">single</option>
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Max Layouts</Form.Label>
                        <Form.Control
                          type="number"
                          min={-1}
                          value={config.layout.maxLayouts}
                          onChange={(e) => {
                            const v = e.target.value;
                            const num = v === '' ? 0 : parseInt(v, 10);
                            if (!Number.isFinite(num)) return;
                            setConfig((prev) => ({ ...prev, layout: { ...prev.layout, maxLayouts: num } }));
                          }}
                          disabled={isReadOnly}
                        />
                        <Form.Text className="text-muted small">−1 = unlimited</Form.Text>
                      </Form.Group>
                      <Form.Check type="switch" label="Allow Create" checked={config.layout.allowCreate} onChange={(e) => setConfig((prev) => ({ ...prev, layout: { ...prev.layout, allowCreate: e.target.checked } }))} disabled={isReadOnly} />
                    </Card>
                  </Col>
                  {(config.setup.allowedItems ?? []).map((i) => String(i).toLowerCase()).includes('workflows') && (
                    <Col md={6}>
                      <Card className="p-3 bg-light">
                        <h6 className="mb-2">Workflow</h6>
                        <Form.Text className="d-block mb-2 text-muted small">Workflow Manager in Setup Menu gives view access. Plan controls max count and create permission only.</Form.Text>
                        <Form.Group className="mb-2">
                          <Form.Label>Max Workflows</Form.Label>
                          <Form.Control
                            type="number"
                            min={-1}
                            value={config.workflow?.maxWorkflows ?? 10}
                            onChange={(e) => {
                              const v = e.target.value;
                              const num = v === '' ? 0 : parseInt(v, 10);
                              if (!Number.isFinite(num)) return;
                              setConfig((prev) => ({ ...prev, workflow: { ...prev.workflow, maxWorkflows: num } }));
                            }}
                            disabled={isReadOnly}
                          />
                          <Form.Text className="text-muted small">−1 = unlimited</Form.Text>
                        </Form.Group>
                        <Form.Check type="switch" label="Allow Create Workflow" checked={config.workflow?.allowCreateWorkflow !== false} onChange={(e) => setConfig((prev) => ({ ...prev, workflow: { ...prev.workflow, allowCreateWorkflow: e.target.checked } }))} disabled={isReadOnly} />
                      </Card>
                    </Col>
                  )}
                  <Col md={6}>
                    <Card className="p-3 bg-light">
                      <h6 className="mb-2">Scoring Rules</h6>
                      <Form.Text className="d-block mb-2 text-muted small">Which objects can have scoring rules. Only objects in Allowed Objects Permission are listed.</Form.Text>
                      <Form.Check type="switch" label="Allow View Scoring Rules" checked={config.scoringRule?.allowViewScoringRule !== false} onChange={(e) => setConfig((prev) => ({ ...prev, scoringRule: { ...prev.scoringRule, allowViewScoringRule: e.target.checked } }))} disabled={isReadOnly} />
                      <Form.Check type="switch" label="Allow Create / Edit Scoring Rules" checked={config.scoringRule?.allowCreateScoringRule !== false} onChange={(e) => setConfig((prev) => ({ ...prev, scoringRule: { ...prev.scoringRule, allowCreateScoringRule: e.target.checked } }))} disabled={isReadOnly} />
                      <Form.Group className="mb-0 mt-2">
                        <Form.Label>Objects allowed for scoring rules</Form.Label>
                        <MultiSelectDropdown
                          picklistValues={scoringRulePicklistOptions}
                          selectedValues={config.scoringRule?.allowedObjects ?? []}
                          onChange={(values) => setConfig((prev) => ({ ...prev, scoringRule: { ...prev.scoringRule, allowedObjects: values } }))}
                          disabled={isReadOnly}
                          placeholder="Select objects..."
                        />
                      </Form.Group>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="p-3 bg-light">
                      <h6 className="mb-2">Assignment Rules</h6>
                      <Form.Text className="d-block mb-2 text-muted small">Which objects can have assignment rules. Only objects in Allowed Objects Permission are listed.</Form.Text>
                      <Form.Check type="switch" label="Allow View Assignment Rules" checked={config.assignmentRule?.allowViewAssignmentRule !== false} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentRule: { ...prev.assignmentRule, allowViewAssignmentRule: e.target.checked } }))} disabled={isReadOnly} />
                      <Form.Check type="switch" label="Allow Create / Edit Assignment Rules" checked={config.assignmentRule?.allowCreateAssignmentRule !== false} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentRule: { ...prev.assignmentRule, allowCreateAssignmentRule: e.target.checked } }))} disabled={isReadOnly} />
                      <Form.Group className="mb-0 mt-2">
                        <Form.Label>Objects allowed for assignment rules</Form.Label>
                        <MultiSelectDropdown
                          picklistValues={scoringRulePicklistOptions}
                          selectedValues={config.assignmentRule?.allowedObjects ?? []}
                          onChange={(values) => setConfig((prev) => ({ ...prev, assignmentRule: { ...prev.assignmentRule, allowedObjects: values } }))}
                          disabled={isReadOnly}
                          placeholder="Select objects..."
                        />
                      </Form.Group>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="p-3 bg-light">
                      <h6 className="mb-2">Web Forms</h6>
                      <Form.Text className="d-block mb-2 text-muted small">Which objects can have web forms. Only objects in Allowed Objects Permission are listed.</Form.Text>
                      <Form.Check type="switch" label="Allow View Web Forms" checked={config.webform?.allowViewWebform !== false} onChange={(e) => setConfig((prev) => ({ ...prev, webform: { ...prev.webform, allowViewWebform: e.target.checked } }))} disabled={isReadOnly} />
                      <Form.Check type="switch" label="Allow Create / Edit Web Forms" checked={config.webform?.allowCreateWebform !== false} onChange={(e) => setConfig((prev) => ({ ...prev, webform: { ...prev.webform, allowCreateWebform: e.target.checked } }))} disabled={isReadOnly} />
                      <Form.Group className="mb-0 mt-2">
                        <Form.Label>Objects allowed for web forms</Form.Label>
                        <MultiSelectDropdown
                          picklistValues={scoringRulePicklistOptions}
                          selectedValues={config.webform?.allowedObjects ?? []}
                          onChange={(values) => setConfig((prev) => ({ ...prev, webform: { ...prev.webform, allowedObjects: values } }))}
                          disabled={isReadOnly}
                          placeholder="Select objects..."
                        />
                      </Form.Group>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="p-3 bg-light">
                      <h6 className="mb-2">Search Views</h6>
                      <Form.Text className="d-block mb-2 text-muted small">
                        Which objects can have search views configured. Without a tenant schema, custom vs standard follows the catalog. Always-migrated objects are omitted here.
                      </Form.Text>
                      <Form.Check type="switch" label="Allow View Search Views" checked={config.searchView?.allowViewSearchView !== false} onChange={(e) => setConfig((prev) => ({ ...prev, searchView: { ...prev.searchView, allowViewSearchView: e.target.checked } }))} disabled={isReadOnly} />
                      <Form.Check type="switch" label="Allow Create / Edit Search Views" checked={config.searchView?.allowCreateSearchView !== false} onChange={(e) => setConfig((prev) => ({ ...prev, searchView: { ...prev.searchView, allowCreateSearchView: e.target.checked } }))} disabled={isReadOnly} />
                      <Form.Group className="mb-0 mt-2">
                        <Form.Label>Standard objects</Form.Label>
                        {searchViewStandardPicklist.length > 0 ? (
                          <MultiSelectDropdown
                            picklistValues={searchViewStandardPicklist}
                            selectedValues={searchViewSelectedStandard}
                            onChange={(values) =>
                              setConfig((prev) => {
                                const custom = (prev.searchView?.allowedObjects ?? []).filter((k) =>
                                  isSearchViewCustomObjectKey(k, standardCatalogKeySet, undefined)
                                );
                                return {
                                  ...prev,
                                  searchView: { ...prev.searchView, allowedObjects: [...new Set([...(values ?? []), ...custom])] },
                                };
                              })
                            }
                            disabled={isReadOnly}
                            placeholder="Select standard objects..."
                          />
                        ) : (
                          <Form.Text className="text-muted small d-block">
                            No standard objects in this list. Add objects under Allowed Objects Permission.
                          </Form.Text>
                        )}
                      </Form.Group>
                      <Form.Group className="mb-0 mt-2">
                        <Form.Label>Custom objects</Form.Label>
                        {searchViewCustomPicklist.length > 0 ? (
                          <MultiSelectDropdown
                            picklistValues={searchViewCustomPicklist}
                            selectedValues={searchViewSelectedCustom}
                            onChange={(values) =>
                              setConfig((prev) => {
                                const std = (prev.searchView?.allowedObjects ?? []).filter(
                                  (k) => !isSearchViewCustomObjectKey(k, standardCatalogKeySet, undefined)
                                );
                                return {
                                  ...prev,
                                  searchView: { ...prev.searchView, allowedObjects: [...new Set([...std, ...(values ?? [])])] },
                                };
                              })
                            }
                            disabled={isReadOnly}
                            placeholder="Select custom objects..."
                          />
                        ) : (
                          <Form.Text className="text-muted small d-block">
                            Plan editor has no tenant schema: custom objects are inferred from the catalog only. Add non-catalog object names to Allowed Objects
                            Permission to see them here, or use Company Subscription override (tenant <code className="small">object_config.is_standard</code>).
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="p-3 bg-light">
                      <h6 className="mb-2">Lead Conversion and Migration</h6>
                      <Form.Check type="switch" label="Allow Mapping" checked={config.leadConversionMapping.allow} onChange={(e) => setConfig((prev) => ({ ...prev, leadConversionMapping: { ...prev.leadConversionMapping, allow: e.target.checked } }))} disabled={isReadOnly} />
                      <Form.Check type="switch" label="Data Migration" checked={config.migration.allow} onChange={(e) => setConfig((prev) => ({ ...prev, migration: { ...prev.migration, allow: e.target.checked } }))} disabled={isReadOnly} className="mt-2" />
                      <Form.Group className="mb-2 mt-2">
                        <Form.Label>Mode</Form.Label>
                        <Form.Select
                          value={config.leadConversionMapping.mode}
                          onChange={(e) =>
                            setConfig((prev) => ({
                              ...prev,
                              leadConversionMapping: { ...prev.leadConversionMapping, mode: e.target.value },
                            }))
                          }
                          disabled={isReadOnly}
                        >
                          <option value="allowlist">allowlist</option>
                          <option value="denylist">denylist</option>
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Max Objects</Form.Label>
                        <Form.Control
                          type="number"
                          value={config.leadConversionMapping.maxObjects}
                          onChange={(e) =>
                            setConfig((prev) => ({
                              ...prev,
                              leadConversionMapping: { ...prev.leadConversionMapping, maxObjects: Number(e.target.value) || 0 },
                            }))
                          }
                          disabled={isReadOnly}
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Objects for lead conversion</Form.Label>
                        <MultiSelectDropdown
                          picklistValues={leadConversionPicklistOptions}
                          selectedValues={config.leadConversionMapping.allowedObjects ?? ['account', 'contact', 'opportunity']}
                          onChange={(values) =>
                            setConfig((prev) => ({
                              ...prev,
                              leadConversionMapping: { ...prev.leadConversionMapping, allowedObjects: values },
                            }))
                          }
                          disabled={isReadOnly}
                          placeholder="Select objects..."
                        />
                      </Form.Group>
                      <Form.Text className="text-muted small">Choose which of the configured Allowed Objects Permission can be used for lead conversion. Default: Account, Contact, Opportunity.</Form.Text>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="p-3 bg-light">
                      <h6 className="mb-2">Object Manager</h6>
                      <Form.Text className="d-block mb-2 text-muted small">
                        Uses Allowed Objects Permission above. Without Setup, users only see those objects (no add/remove fields or layout).
                      </Form.Text>
                      <Form.Check type="switch" label="Allow Create" checked={!!config.objectManager.allow_create} onChange={(e) => setConfig((prev) => ({ ...prev, objectManager: { ...prev.objectManager, allow_create: e.target.checked } }))} disabled={isReadOnly} className="mb-2" />
                      <Form.Check type="switch" label="Enable Standard" checked={!!config.objectManager.enable_standard} onChange={(e) => setConfig((prev) => ({ ...prev, objectManager: { ...prev.objectManager, enable_standard: e.target.checked } }))} disabled={isReadOnly} className="mb-2" />
                    </Card>
                  </Col>
                  <Col xs={12}>
                    <Card className="p-3 bg-light">
                      <h6 className="mb-2">Object Permissions</h6>
                      <Form.Text className="d-block mb-2 text-muted small">
                        Define per-object permissions (Create, Read, Update, Delete, View All, Modify All). Same UI as role PermissionBulkEdit. Plan licenses inherit and can restrict further.
                      </Form.Text>
                      <ObjectPermissionMatrix
                        objects={stripDisabledPackageObjects(
                          config.enabledPackages,
                          config.allowedObjectsPermission ?? []
                        ).map((name) => {
                          const opt = standardPicklistOptions.find((o) => String(o.key).toLowerCase() === String(name).toLowerCase());
                          const displayLabel = opt?.value || (String(name).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || name);
                          return { object_name: name, object_display_name: displayLabel };
                        })}
                        permissions={config.objectPermissions ?? {}}
                        onChange={(objectPermissions) =>
                          setConfig((prev) => ({ ...prev, objectPermissions }))
                        }
                        disabled={isReadOnly}
                        showSearch={true}
                      />
                    </Card>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          <Row className="g-3">
            <Col md={6}>
              <Card className="p-3">
                <h5>Report</h5>
                <Form.Text className="d-block mb-2 text-muted small">Report is not part of Setup; configure regardless of Setup.</Form.Text>
                <Form.Group className="mb-2">
                  <Form.Label>Max Reports</Form.Label>
                  <Form.Control
                    type="number"
                    min={-1}
                    value={config.report?.maxReports ?? 10}
                    onChange={(e) => {
                      const v = e.target.value;
                      const num = v === '' ? 0 : parseInt(v, 10);
                      if (!Number.isFinite(num)) return;
                      setConfig((prev) => ({ ...prev, report: { ...prev.report, maxReports: num } }));
                    }}
                    disabled={isReadOnly}
                  />
                  <Form.Text className="text-muted small">−1 = unlimited</Form.Text>
                </Form.Group>
                <Form.Check type="switch" label="Allow Create Report" checked={config.report?.allowCreateReport !== false} onChange={(e) => setConfig((prev) => ({ ...prev, report: { ...prev.report, allowCreateReport: e.target.checked } }))} disabled={isReadOnly} />
              </Card>
            </Col>
            <Col md={6}>
              <Card className="p-3">
                <h5>Dashboard</h5>
                <Form.Text className="d-block mb-2 text-muted small">Dashboard is not part of Setup; configure regardless of Setup.</Form.Text>
                <Form.Group className="mb-2">
                  <Form.Label>Max Dashboards</Form.Label>
                  <Form.Control
                    type="number"
                    min={-1}
                    value={config.dashboard?.maxDashboards ?? 5}
                    onChange={(e) => {
                      const v = e.target.value;
                      const num = v === '' ? 0 : parseInt(v, 10);
                      if (!Number.isFinite(num)) return;
                      setConfig((prev) => ({ ...prev, dashboard: { ...prev.dashboard, maxDashboards: num } }));
                    }}
                    disabled={isReadOnly}
                  />
                  <Form.Text className="text-muted small">−1 = unlimited</Form.Text>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Max assigned dashboards per user</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={config.dashboard?.maxAssignedDashboardsPerUser ?? 2}
                    onChange={(e) => {
                      const v = e.target.value;
                      const num = v === '' ? 2 : parseInt(v, 10);
                      if (!Number.isFinite(num) || num < 1) return;
                      setConfig((prev) => ({
                        ...prev,
                        dashboard: { ...prev.dashboard, maxAssignedDashboardsPerUser: num },
                      }));
                    }}
                    disabled={isReadOnly}
                  />
                  <Form.Text className="text-muted small">
                    How many dashboards a user can be assigned to across the org (default 2).
                  </Form.Text>
                </Form.Group>
                <Form.Check type="switch" label="Allow Create Dashboard" checked={config.dashboard?.allowCreateDashboard !== false} onChange={(e) => setConfig((prev) => ({ ...prev, dashboard: { ...prev.dashboard, allowCreateDashboard: e.target.checked } }))} disabled={isReadOnly} />
              </Card>
            </Col>
          </Row>

          <Row className="g-3">
            <Col xs={12}>
              <StorageLimitsFields
                storage={config.storage}
                onChange={setConfig}
                disabled={isReadOnly}
              />
            </Col>
          </Row>

          <div className="mt-3 d-flex justify-content-end">
            {editMode ? (
              <>
                <Button className="admin-primary-btn" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="secondary" onClick={handleCancel} disabled={saving} className="ms-2">
                  Cancel
                </Button>
              </>
            ) : (
              <Button className="admin-primary-btn" onClick={() => setEditMode(true)} disabled={loading || saving}>
                Edit
              </Button>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default AdminPlanConfig;
